/*eslint-env browser*/
import topLeftAndPivot from "./topleft_and_pivot.js"
import autoDimensions from "./auto_dimensions.js"

const {getTopLeftPoint, containsPositionParam, copyPositionParam} = topLeftAndPivot

//with top left corner at x = 0, y = 0 (canvas convention)
function autoRectangleParams (params, sizeParams) { //eslint-disable-line complexity
    if (params.topLeft) {
        let {
            fullWidth,
            fullHeight,
            width  = Math.max(0, fullWidth  - params.topLeft.x),
            height = Math.max(0, fullHeight - params.topLeft.y)
        } = sizeParams
        return {
            topLeftX: params.topLeft.x,
            topLeftY: params.topLeft.y,
            width,
            height
        }
    } else if (params.center) {
        let {
            fullWidth,
            fullHeight,
            width  = Math.max(0, Math.min(params.center.x, fullWidth  - params.center.x) * 2),
            height = Math.max(0, Math.min(params.center.y, fullHeight - params.center.y) * 2)
        } = sizeParams
        return {
            topLeftX: params.center.x - width / 2,
            topLeftY: params.center.y - height / 2,
            width,
            height
        }
    } else if (params.topRight) {
        let {
            fullHeight,
            width  = Math.max(0, params.topRight.x),
            height = Math.max(0, fullHeight - params.topRight.y)
        } = sizeParams
        return {
            topLeftX: params.topRight.x - width,
            topLeftY: params.topRight.y,
            width,
            height
        }
    } else if (params.bottomLeft) {
        let {
            fullWidth,
            width  = Math.max(0, fullWidth - params.bottomLeft.x),
            height = Math.max(0, params.bottomLeft.y)
        } = sizeParams
        return {
            topLeftX: params.bottomLeft.x,
            topLeftY: params.bottomLeft.y - height,
            width,
            height
        }
    } else if (params.bottomRight) {
        let {
            width  = Math.max(0, params.bottomRight.x),
            height = Math.max(0, params.bottomRight.y)
        } = sizeParams
        return {
            topLeftX: params.bottomRight.x - width,
            topLeftY: params.bottomRight.y - height,
            width,
            height
        }
    } else if (params.centerLeft || params.left) {
        let {x, y} = params.centerLeft || params.left
        let {
            fullWidth,
            fullHeight,
            width  = Math.max(0, fullWidth - x),
            height = Math.max(0, Math.min(y, fullHeight - y) * 2)
        } = sizeParams
        return {
            topLeftX: x,
            topLeftY: y - height / 2,
            width,
            height
        }
    } else if (params.centerRight || params.right) {
        let {x, y} = params.centerRight || params.right
        let {
            fullHeight,
            width  = Math.max(0, x),
            height = Math.max(0, Math.min(y, fullHeight - y) * 2)
        } = sizeParams
        return {
            topLeftX: x - width,
            topLeftY: y - height / 2,
            width,
            height
        }
    } else if (params.bottomCenter || params.bottom) {
        let {x, y} = params.bottomCenter || params.bottom
        let {
            fullWidth,
            width  = Math.max(0, Math.min(x, fullWidth - x) * 2),
            height = Math.max(0, y)
        } = sizeParams
        return {
            topLeftX: x - width / 2,
            topLeftY: y - height,
            width,
            height
        }
    } else if (params.topCenter || params.top) {
        let {x, y} = params.topCenter || params.top
        let {
            fullWidth,
            fullHeight,
            width  = Math.max(0, Math.min(x, fullWidth - x) * 2),
            height = Math.max(0, fullHeight - y)
        } = sizeParams
        return {
            topLeftX: x - width / 2,
            topLeftY: y,
            width,
            height
        }
    } else {
        let {fullWidth, fullHeight, width = fullWidth, height = fullHeight} = sizeParams
        return {
            topLeftX: 0,
            topLeftY: 0,
            width,
            height
        }
    }

}


function getCropParams ({width: fullWidth, height: fullHeight}, cropSource) {

    if (!cropSource) {
        return {
            topLeftX: 0,
            topLeftY: 0,
            width:    fullWidth,
            height:   fullHeight,
            cropped:  false
        }
    }

    let {topLeftX, topLeftY, width, height} = autoRectangleParams(cropSource, {
        width:  cropSource.width,
        height: cropSource.height,
        fullWidth,
        fullHeight
    })

    let cropped = (
        topLeftX !== 0 ||
        topLeftY !== 0 ||
        width    !== fullWidth ||
        height   !== fullHeight
    )

    return {topLeftX, topLeftY, width, height, cropped}
}


function getCropParamsForTexture ({width: fullWidth, height: fullHeight}, cropSource) {

    if (!cropSource) {
        return {
            topLeftX: 0,
            topLeftY: 0,
            width:    1,
            height:   1
        }
    }

    let {topLeftX, topLeftY, width, height} = autoRectangleParams(cropSource, {
        width:  cropSource.width,
        height: cropSource.height,
        fullWidth,
        fullHeight
    })

    topLeftX /= fullWidth
    topLeftY /= fullHeight
    width    /= fullWidth
    height   /= fullHeight


    return {topLeftX, topLeftY, width, height}
}



function getScaleAndCropParams ({width: fullWidth, height: fullHeight}, params, cropOptionName) {

    const cropSource = getCropParams({width: fullWidth, height: fullHeight}, params[cropOptionName])

    let {widthInPixels, heightInPixels, pixelSize} = params

    if (pixelSize) {
        let {width, height} = autoDimensions(Object.assign({
            sourceWidth:  cropSource.width,
            sourceHeight: cropSource.height
        }, params))
        widthInPixels  = Math.ceil(width  / pixelSize)
        heightInPixels = Math.ceil(height / pixelSize)
    }

    let newDimensions = autoDimensions({
        width:        widthInPixels,
        height:       heightInPixels,
        sourceWidth:  cropSource.width,
        sourceHeight: cropSource.height
    })

    widthInPixels  = Math.round(newDimensions.width)
    heightInPixels = Math.round(newDimensions.height)


    const rescaled  = (widthInPixels !== fullWidth || heightInPixels !== fullHeight)
    const {cropped} = cropSource

    return {
        cropSource,
        flip: params.flip,
        widthInPixels,
        heightInPixels,
        rescaled,
        cropped
    }
}


function addImageDefaultPositionParams (params, image) {
    let defaultParams = image
    if (image.getCamera) { //FIXME this is for screen
        defaultParams = image.getCamera()
    }

    if (!containsPositionParam(params)) {
        copyPositionParam(defaultParams, params)
    }
    if (!("width" in params) && !("height" in params)) {
        params.width  = defaultParams.width
        params.height = defaultParams.height
    }
}



function getPositionParamsForImage ({smartObject, source, params, reverseY, forceTopLeft, flip: newFlip}) { //eslint-disable-line complexity

    let positionData = smartObject

    if (smartObject.getCamera) {
        positionData = smartObject.getCamera()
    }


    let {width, height, cropSource, flip = newFlip, orientation} = params

    if (typeof width === "undefined" && typeof height === "undefined") {
        width  = positionData.width
        height = positionData.height
    }


    if (smartObject.imageData) {
        cropSource = getCropParams({
            width:  smartObject.widthInPixels,
            height: smartObject.heightInPixels
        }, cropSource)
    } else {
        cropSource = getCropParams(source, cropSource || smartObject.cropSource)
    }

    const {width: finalWidth, height: finalHeight} = autoDimensions({
        width,
        height,
        sourceWidth:  cropSource.width,
        sourceHeight: cropSource.height
    })


    const positionParams = {
        cropSource,
        width:  finalWidth,
        height: finalHeight,
        orientation,
        flip
    }

    if (forceTopLeft) {
        let h = finalHeight
        if (reverseY) { //FIXME
            h = -finalHeight
        }
        positionParams.topLeft = getTopLeftPoint(params, finalWidth, h) || getTopLeftPoint(positionData, finalWidth, h)
    } else {
        let copiedPosition = copyPositionParam(params, positionParams)
        if (!copiedPosition) {
            copyPositionParam(positionData, positionParams)
        }
    }

    return positionParams
}


function getUsableImageAndPositionParams (image, params, {reverseY, forceTopLeft} = {}) { //eslint-disable-line complexity

    let smartObject = image
    let source      = image
    let flip

    if (image.forScreen) {
        ({image, smartObject, source, flip} = image.forScreen())
    } else if (isObjectLiteral(image) && image.image) { // for SpriteCollection, or custom objects
        flip   = image.flip
        image  = image.image
        source = image
    }

    if (!image) {
        throw new Error("An image must be specified")
    }


    const positionParams = getPositionParamsForImage({
        smartObject,
        source,
        params,
        reverseY,
        forceTopLeft,
        flip
    })

    return {image, positionParams}
}


function isObjectLiteral (object) {
    return Object.prototype.toString.call(object) === "[object Object]"
}


//"pixelated" is the canonical option ; "smoothing" is its deprecated, inverted alias.
//pixelated wins when both are given.
function resolveSmoothing ({pixelated, smoothing = true}) {
    if (pixelated !== undefined) {
        return !pixelated
    }
    return smoothing
}


const textureConfigKeys = [
    "image", "repeat", "mirror", "scale", "origin", "orientation",
    "followShape", "cropSource", "flip", "opacity", "pixelated", "smoothing",
    "width", "height"
]

function hasTextureConfigShape (object) {
    return textureConfigKeys.some(key => key in object)
}


//texture: "url.png" | <img> | canvas | ScreenImage | Screen | sprite-pack literal | {image, ...config}
function normalizeTexture (value) {
    if (!value) {
        return value
    }
    if (typeof value === "string") {
        return {image: value}
    }
    if (isObjectLiteral(value) && hasTextureConfigShape(value)) {
        return value
    }
    return {image: value}
}



const imageCacheAndQueue = {}


function fetchImage (url, {errorHandler} = {}) {
    let remoteImage = new Image()
    remoteImage.crossOrigin = "Anonymous"
    remoteImage.addEventListener("load", () => {
        imageCacheAndQueue[url].forEach(f => f(remoteImage))
        imageCacheAndQueue[url] = remoteImage
    })

    remoteImage.addEventListener("error", () => {
        imageCacheAndQueue[url] = "error"
        const error = new Error(`Cannot load image ${url}`)
        error.toxiTrace = ["Toxilibs - screen.js - fetchImage"]
        if (errorHandler) {
            errorHandler(error)
        } else {
            throw error
        }

    })

    remoteImage.src = url
}


function getImageByUrl ({url, errorHandler, callback}) {
    const cacheOrQueue = imageCacheAndQueue[url]

    if (!cacheOrQueue) {
        imageCacheAndQueue[url] = [callback]
        fetchImage(url, {errorHandler})
    } else if (Array.isArray(cacheOrQueue)) {
        cacheOrQueue.push(callback)
    } else if (cacheOrQueue !== "error") {
        callback(cacheOrQueue)
    }
}



export default {
    fetchImage,
    getImageByUrl,
    getCropParams,
    getCropParamsForTexture,
    getScaleAndCropParams,
    getUsableImageAndPositionParams,
    addImageDefaultPositionParams,
    normalizeTexture,
    resolveSmoothing
}

